import { useEffect, useState } from "react";
import { api, type UptimeResponse } from "@/lib/api";
import { cn } from "@/lib/utils";

const DAY_MS = 86_400_000;
const DAYS = 30;

function dayKey(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function colorFor(pct: number, total: number, expected: number): string {
  // If we never saw a single check that day, arbiter-admin was offline.
  if (total === 0) return "bg-[var(--color-panel-2)]";
  // Some checks but less than 50% of expected — arbiter-admin was *mostly* down.
  if (total < expected * 0.5) return "bg-[var(--color-bad)]/30";
  if (pct >= 99.5) return "bg-[var(--color-good)]";
  if (pct >= 95) return "bg-[var(--color-warn)]";
  return "bg-[var(--color-bad)]";
}

export function UptimeStrip({ app }: { app: string }) {
  const [data, setData] = useState<UptimeResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.uptime(app, DAYS, "day");
        if (!cancelled) setData(r);
      } catch {
        // silent — strip just stays empty
      }
    };
    load();
    const id = setInterval(load, 5 * 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [app]);

  // Build a row of DAYS cells, oldest → newest, aligned to UTC day boundaries
  // ending at *today*. Match each bucket from the API into its day.
  const todayStart = Math.floor(Date.now() / DAY_MS) * DAY_MS;
  const cells: { day_ms: number; bucket: UptimeResponse["buckets"][number] | null }[] = [];
  const byDay = new Map<number, UptimeResponse["buckets"][number]>(
    (data?.buckets ?? []).map((b) => [b.ts, b]),
  );
  for (let i = DAYS - 1; i >= 0; i--) {
    const day_ms = todayStart - i * DAY_MS;
    cells.push({ day_ms, bucket: byDay.get(day_ms) ?? null });
  }

  const expected = data?.expected_per_bucket ?? 1;

  // Aggregate stats for the header summary
  const observed = cells.filter((c) => c.bucket != null);
  const totalHealthy = observed.reduce((acc, c) => acc + (c.bucket?.healthy ?? 0), 0);
  const totalChecks = observed.reduce((acc, c) => acc + (c.bucket?.total ?? 0), 0);
  const avgPct = totalChecks > 0 ? (100 * totalHealthy) / totalChecks : 0;
  const offlineDays = cells.filter((c) => c.bucket == null).length;

  return (
    <div className="px-5 pb-4">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
          30-day uptime
        </div>
        <div className="text-[10px] font-mono text-[var(--color-muted)]">
          {totalChecks > 0 ? `${avgPct.toFixed(2)}%` : "no data"}
          {offlineDays > 0 && (
            <span className="ml-2 text-[var(--color-bad)]">{offlineDays}d gap</span>
          )}
        </div>
      </div>
      <div className="flex gap-px h-5 rounded overflow-hidden">
        {cells.map((c) => {
          const pct = c.bucket?.uptime_pct ?? 0;
          const total = c.bucket?.total ?? 0;
          const date = dayKey(c.day_ms);
          const title = c.bucket
            ? `${date} — ${pct.toFixed(1)}% (${c.bucket.healthy}/${c.bucket.total} checks)`
            : `${date} — no data (arbiter-admin offline)`;
          return (
            <div
              key={c.day_ms}
              title={title}
              className={cn("flex-1 transition", colorFor(pct, total, expected))}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] text-[var(--color-muted)] mt-1 font-mono">
        <span>{dayKey(cells[0].day_ms)}</span>
        <span>{dayKey(cells[cells.length - 1].day_ms)}</span>
      </div>
    </div>
  );
}
